import { cn } from "@/utils";
import { type LucideIcon, Plus } from "lucide-react";
import { motion } from "motion/react";
import type { ReactNode } from "react";

export type RailZeroStateAccent = "rust" | "sage" | "teal" | "plum";

const accentIconClassName: Record<RailZeroStateAccent, string> = {
  rust: "text-rust",
  sage: "text-sage",
  teal: "text-teal",
  plum: "text-plum",
};

const accentWellClassName: Record<RailZeroStateAccent, string> = {
  rust: "bg-rust/10",
  sage: "bg-sage/10",
  teal: "bg-teal/10",
  plum: "bg-plum/10",
};

export interface RailZeroStateAction {
  label: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  icon?: LucideIcon;
}

export interface RailZeroStateProps {
  icon: LucideIcon;
  accent?: RailZeroStateAccent;
  title: string;
  description: string;
  variant?: "cta" | "passive";
  action?: RailZeroStateAction;
  /** Custom CTA slot (e.g. dropdown trigger). Renders instead of `action` when set. */
  actionSlot?: ReactNode;
  className?: string;
}

/** Compact empty-state block for left-rail accordion sections. */
export function RailZeroState({
  icon: Icon,
  accent = "rust",
  title,
  description,
  variant = "cta",
  action,
  actionSlot,
  className,
}: RailZeroStateProps) {
  const showAction = variant === "cta" && (actionSlot || action);
  const ActionIcon = action?.icon ?? Plus;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
      className={cn("mx-1 px-2 py-1.5", className)}
    >
      <div className="flex items-start gap-2">
        <div
          className={cn(
            "mt-px flex size-5 shrink-0 items-center justify-center rounded-md",
            accentWellClassName[accent],
          )}
        >
          <Icon className={cn("size-3", accentIconClassName[accent])} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium leading-snug text-n-80">{title}</p>
          <p className="mt-0.5 text-xs leading-snug text-n-50">{description}</p>
          {showAction ? (
            actionSlot ? (
              <div className="mt-1.5">{actionSlot}</div>
            ) : action ? (
              <button
                type="button"
                disabled={action.disabled}
                onClick={action.onClick}
                className={cn(
                  "-ml-1.5 mt-1.5 inline-flex min-h-6 items-center gap-1 rounded-md px-1.5 text-xs font-medium text-n-70",
                  "hover:bg-n-05 hover:text-n-90",
                  action.disabled && "pointer-events-none opacity-50",
                )}
              >
                <ActionIcon className="size-3.5" />
                {action.label}
              </button>
            ) : null
          ) : null}
        </div>
      </div>
    </motion.div>
  );
}
